import { Suspense } from "react";
import { LogOut } from "lucide-react";
import { Link, Outlet, useNavigate } from "react-router";

import { PageLoader } from "@/components/PageLoader";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { PROTECTED_ROUTES, ROUTES } from "@/constants/routes";
import { NotificationBell } from "@/features/engagement/components/NotificationBell";
import { WishlistIcon } from "@/features/engagement/components/WishlistIcon";
import { useLogout } from "@/features/identity/hooks/useLogout";
import { ChatIcon } from "@/features/messaging/components/ChatIcon";
import { useAppSelector } from "@/store/hooks";

function initialsFromName(name: string): string {
  const [first, second] = name.trim().split(/\s+/);
  return `${first?.[0] ?? ""}${second?.[0] ?? ""}`.toUpperCase() || "?";
}

/**
 * docs/ui/layouts.md's `AccountLayout` — used by every Protected route (My
 * Listings, Wishlist, Chat, Notifications, Profile, Settings, Verification
 * Status). The desktop `AccountSidebarNav` and mobile `BottomNav` are real,
 * well-defined item sets per `ui-architecture.md` §10/§11 — still not built
 * here, since there are no pages yet for those links to point at.
 *
 * Every route under this layout is wrapped in `RequireAuth`
 * (src/routes/router.tsx), so `state.auth.user` is always populated by the
 * time this renders — the Avatar shows real initials, not a placeholder.
 */
export function AuthenticatedLayout() {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const logoutMutation = useLogout();

  function handleLogout() {
    logoutMutation.mutate(undefined, {
      onSettled: () => navigate(ROUTES.login, { replace: true }),
    });
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-border px-4">
        <Link to={ROUTES.home} className="text-h3 text-foreground">
          BazaarX
        </Link>
        <div className="flex items-center gap-3">
          <Button type="button" size="sm" asChild>
            <Link to={PROTECTED_ROUTES.sellMedia}>Sell</Link>
          </Button>
          <WishlistIcon />
          <ChatIcon />
          <NotificationBell />
          <ThemeToggle />
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to={PROTECTED_ROUTES.profile} aria-label="Your profile">
                <Avatar size="md">
                  {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt="" />}
                  <AvatarFallback>{user ? initialsFromName(user.name) : null}</AvatarFallback>
                </Avatar>
              </Link>
            </TooltipTrigger>
            <TooltipContent>Profile</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Log out"
                onClick={handleLogout}
                isLoading={logoutMutation.isPending}
              >
                <LogOut aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Log out</TooltipContent>
          </Tooltip>
        </div>
      </header>
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
